import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { formatPercent } from '../../util/format';
import { useTranslation } from '../../i18n';

/**
 * ProgressBar - Horizontal progress bar with label
 *
 * @param {string} label - Label text
 * @param {number} value - Current value
 * @param {number} total - Total value (for percentage calculation)
 * @param {string} color - Bar color
 * @param {boolean} showPercentage - Show percentage text
 */
const ProgressBar = ({
  label,
  value,
  total,
  color = '#FFC451',
  showPercentage = true,
  height = 8,
  style,
}) => {
  const { t } = useTranslation('analytics');

  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <View style={[styles.container, style]}>
      <View style={styles.header}>
        <Text style={styles.label}>{label}</Text>
        <Text style={styles.value}>
          {value}
          {showPercentage && ` (${formatPercent(percentage)})`}
        </Text>
      </View>
      <View style={[styles.track, { height }]}>
        <View
          style={[
            styles.fill,
            {
              width: `${Math.min(percentage, 100)}%`,
              backgroundColor: color,
              height,
            },
          ]}
        />
      </View>
    </View>
  );
};

/**
 * ProgressBarGroup - Multiple progress bars for comparison
 *
 * @param {string} title - Section title
 * @param {Array} items - Array of { label, value, color }
 * @param {number} defaultVisible - Number of items to show by default (0 = show all)
 * @param {Object} style - Optional additional styles
 */
export const ProgressBarGroup = ({ title, items, defaultVisible = 5, style }) => {
  const { t } = useTranslation('analytics');
  const [showAll, setShowAll] = useState(false);
  const maxValue = Math.max(...items.map((i) => i.value), 1);

  const hasMore = items.length > defaultVisible;
  const displayItems = showAll || !hasMore ? items : items.slice(0, defaultVisible);

  return (
    <View style={[styles.groupContainer, style]}>
      {title && <Text style={styles.groupTitle}>{title}</Text>}
      {displayItems.map((item, index) => (
        <ProgressBar
          key={index}
          label={item.label}
          value={item.value}
          total={maxValue}
          color={item.color}
          showPercentage={false}
          style={index < displayItems.length - 1 ? { marginBottom: 12 } : {}}
        />
      ))}
      {hasMore && (
        <Pressable
          onPress={() => setShowAll(!showAll)}
          style={({ pressed, hovered, focused }) => [
            styles.toggleButton,
            { cursor: 'pointer' },
            (hovered || focused) && styles.toggleButtonHovered,
            pressed && styles.toggleButtonPressed,
          ]}
        >
          <Text style={styles.toggleText}>
            {showAll ? t('showLess') : t('showAllCount', { count: items.length })}
          </Text>
          <Feather
            name={showAll ? 'chevron-up' : 'chevron-down'}
            size={16}
            color="#6B7280"
          />
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {},
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  label: {
    fontSize: 13,
    color: '#374151',
    flex: 1,
  },
  value: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  track: {
    backgroundColor: '#f3f4f6',
    borderRadius: 4,
    overflow: 'hidden',
  },
  fill: {
    borderRadius: 4,
  },
  // V3: Card styling matches Submit Report cards
  groupContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F0F0F0',
  },
  groupTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
  },
  toggleButtonHovered: {
    backgroundColor: '#ECECEC',
  },
  toggleButtonPressed: {
    backgroundColor: '#E5E5E5',
  },
  toggleText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6B7280',
    marginRight: 4,
  },
});

export default ProgressBar;
